import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { ColumnType } from '@projectflow/shared';

// Types
interface SheetColumn {
  id: string;
  sheetId: string;
  name: string;
  columnType: ColumnType;
  position: number;
  width: number;
  isHidden: boolean;
  isLocked: boolean;
  isPrimary: boolean;
  isRequired: boolean;
  defaultValue: unknown | null;
  options: ColumnOptions;
  formula: string | null;
  validation: ColumnValidation | null;
  createdAt: string;
  updatedAt: string;
}

interface ColumnOptions {
  choices?: Array<{ value: string; label: string; color?: string }>;
  allowMultiple?: boolean;
  decimalPlaces?: number;
  thousandsSeparator?: boolean;
  currencyCode?: string;
  currencySymbol?: string;
  includeTime?: boolean;
  dateFormat?: string;
  durationFormat?: 'hours' | 'days' | 'weeks' | 'months';
  allowMultipleContacts?: boolean;
  showProgressBar?: boolean;
  prefix?: string;
  startNumber?: number;
}

interface ColumnValidation {
  type: 'range' | 'list' | 'regex' | 'custom';
  min?: number;
  max?: number;
  allowedValues?: string[];
  pattern?: string;
  errorMessage?: string;
}

interface CreateColumnDto {
  name: string;
  columnType: ColumnType;
  position?: number;
  width?: number;
  isRequired?: boolean;
  defaultValue?: unknown;
  options?: ColumnOptions;
  formula?: string;
  validation?: ColumnValidation;
}

interface UpdateColumnDto {
  name?: string;
  position?: number;
  width?: number;
  isHidden?: boolean;
  isLocked?: boolean;
  isRequired?: boolean;
  defaultValue?: unknown;
  options?: ColumnOptions;
  formula?: string;
  validation?: ColumnValidation;
}

// Query keys
export const columnKeys = {
  all: ['columns'] as const,
  lists: () => [...columnKeys.all, 'list'] as const,
  list: (sheetId: string) => [...columnKeys.lists(), sheetId] as const,
  detail: (columnId: string) => [...columnKeys.all, 'detail', columnId] as const,
};

// Fetch columns for a sheet
export function useColumnsQuery(sheetId: string) {
  return useQuery({
    queryKey: columnKeys.list(sheetId),
    queryFn: async () => {
      const response = await api.get<SheetColumn[]>(
        `/sheets/${sheetId}/columns`
      );
      return response.data;
    },
    enabled: !!sheetId,
    staleTime: 60000, // 1 minute - columns change less frequently
  });
}

// Create column mutation
export function useCreateColumn(sheetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dto: CreateColumnDto) => {
      const response = await api.post<SheetColumn>(
        `/sheets/${sheetId}/columns`,
        dto
      );
      return response.data;
    },
    onMutate: async (newColumn) => {
      await queryClient.cancelQueries({ queryKey: columnKeys.list(sheetId) });

      const previousColumns = queryClient.getQueryData<SheetColumn[]>(
        columnKeys.list(sheetId)
      );

      if (previousColumns) {
        const maxPosition = Math.max(
          0,
          ...previousColumns.map((c) => c.position)
        );
        const optimisticColumn: SheetColumn = {
          id: `temp-${Date.now()}`,
          sheetId,
          name: newColumn.name,
          columnType: newColumn.columnType,
          position: newColumn.position ?? maxPosition + 1,
          width: newColumn.width ?? 150,
          isHidden: false,
          isLocked: false,
          isPrimary: false,
          isRequired: newColumn.isRequired ?? false,
          defaultValue: newColumn.defaultValue ?? null,
          options: newColumn.options ?? {},
          formula: newColumn.formula ?? null,
          validation: newColumn.validation ?? null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        queryClient.setQueryData<SheetColumn[]>(columnKeys.list(sheetId), [
          ...previousColumns,
          optimisticColumn,
        ]);
      }

      return { previousColumns };
    },
    onError: (_err, _newColumn, context) => {
      if (context?.previousColumns) {
        queryClient.setQueryData(
          columnKeys.list(sheetId),
          context.previousColumns
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: columnKeys.list(sheetId) });
    },
  });
}

// Update column mutation
export function useUpdateColumn(sheetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      columnId,
      updates,
    }: {
      columnId: string;
      updates: UpdateColumnDto;
    }) => {
      const response = await api.patch<SheetColumn>(
        `/columns/${columnId}`,
        updates
      );
      return response.data;
    },
    onMutate: async ({ columnId, updates }) => {
      await queryClient.cancelQueries({ queryKey: columnKeys.list(sheetId) });

      const previousColumns = queryClient.getQueryData<SheetColumn[]>(
        columnKeys.list(sheetId)
      );

      if (previousColumns) {
        queryClient.setQueryData<SheetColumn[]>(
          columnKeys.list(sheetId),
          previousColumns.map((col) =>
            col.id === columnId
              ? { ...col, ...updates, updatedAt: new Date().toISOString() }
              : col
          )
        );
      }

      return { previousColumns };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousColumns) {
        queryClient.setQueryData(
          columnKeys.list(sheetId),
          context.previousColumns
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: columnKeys.list(sheetId) });
    },
  });
}

// Delete column mutation
export function useDeleteColumn(sheetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (columnId: string) => {
      await api.delete(`/columns/${columnId}`);
      return columnId;
    },
    onMutate: async (columnId) => {
      await queryClient.cancelQueries({ queryKey: columnKeys.list(sheetId) });

      const previousColumns = queryClient.getQueryData<SheetColumn[]>(
        columnKeys.list(sheetId)
      );

      if (previousColumns) {
        queryClient.setQueryData<SheetColumn[]>(
          columnKeys.list(sheetId),
          previousColumns.filter((col) => col.id !== columnId)
        );
      }

      return { previousColumns };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousColumns) {
        queryClient.setQueryData(
          columnKeys.list(sheetId),
          context.previousColumns
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: columnKeys.list(sheetId) });
    },
  });
}

// Reorder columns mutation
export function useReorderColumns(sheetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (columnOrder: string[]) => {
      const response = await api.patch<SheetColumn[]>(
        `/sheets/${sheetId}/columns/reorder`,
        { columnOrder }
      );
      return response.data;
    },
    onMutate: async (columnOrder) => {
      await queryClient.cancelQueries({ queryKey: columnKeys.list(sheetId) });

      const previousColumns = queryClient.getQueryData<SheetColumn[]>(
        columnKeys.list(sheetId)
      );

      if (previousColumns) {
        const orderMap = new Map(columnOrder.map((id, index) => [id, index]));
        const reordered = [...previousColumns].sort((a, b) => {
          const posA = orderMap.get(a.id) ?? a.position;
          const posB = orderMap.get(b.id) ?? b.position;
          return posA - posB;
        });

        // Update positions
        const withNewPositions = reordered.map((col, index) => ({
          ...col,
          position: index,
        }));

        queryClient.setQueryData<SheetColumn[]>(
          columnKeys.list(sheetId),
          withNewPositions
        );
      }

      return { previousColumns };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousColumns) {
        queryClient.setQueryData(
          columnKeys.list(sheetId),
          context.previousColumns
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: columnKeys.list(sheetId) });
    },
  });
}

// Resize column (local + API)
export function useResizeColumn(sheetId: string) {
  const updateColumn = useUpdateColumn(sheetId);

  return {
    resize: (columnId: string, width: number) => {
      return updateColumn.mutate({ columnId, updates: { width } });
    },
    isResizing: updateColumn.isPending,
  };
}

// Toggle column visibility
export function useToggleColumnVisibility(sheetId: string) {
  const updateColumn = useUpdateColumn(sheetId);

  return {
    toggle: (columnId: string, isHidden: boolean) => {
      return updateColumn.mutate({ columnId, updates: { isHidden } });
    },
    isToggling: updateColumn.isPending,
  };
}

export type { SheetColumn, ColumnOptions, ColumnValidation, CreateColumnDto, UpdateColumnDto };
